import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Photo } from '../types';
import { getOptimizedImageUrl } from '../utils/imageOptimization';

// Random durations between 3000ms and 5000ms for smoother transitions
const getRandomDuration = () => Math.random() * 2000 + 3000;

interface ImageReelProps {
  photos: Photo[];
  onReady?: () => void;
}

export function ImageReel({ photos, onReady }: ImageReelProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(0);
  const [showCurrent, setShowCurrent] = useState(true);
  const [duration, setDuration] = useState(getRandomDuration());
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const readyFiredRef = useRef(false);

  const fireReady = () => {
    if (!readyFiredRef.current) {
      readyFiredRef.current = true;
      onReadyRef.current?.();
    }
  };

  // Preload all images to prevent flickering
  useEffect(() => {
    if (photos.length === 0) {
      // No photos to load — signal ready immediately
      fireReady();
      return;
    }

    let loadedCount = 0;
    const totalImages = Math.min(photos.length, 5); // Preload first 5 images

    photos.slice(0, 5).forEach((photo) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
          fireReady();
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesLoaded(true);
          fireReady();
        }
      };
      img.src = getOptimizedImageUrl(photo.imageUrl, 'hero');
    });

    // Fallback: show after 2 seconds even if not all loaded
    const fallbackTimeout = setTimeout(() => {
      setImagesLoaded(true);
      fireReady();
    }, 2000);

    return () => clearTimeout(fallbackTimeout);
  }, [photos]);

  // Initialize nextIndex properly
  useEffect(() => {
    if (photos.length > 1) {
      setNextIndex(1);
    }
  }, [photos.length]);

  useEffect(() => {
    if (photos.length === 0 || !imagesLoaded) return;
    
    // If only one photo, don't transition
    if (photos.length === 1) return;
    
    const timeout = setTimeout(() => {
      // Toggle between current and next
      setShowCurrent(prev => !prev);
      
      // Update indices after fade starts
      setTimeout(() => {
        if (showCurrent) {
          setNextIndex((nextIndex + 1) % photos.length);
        } else {
          setCurrentIndex((currentIndex + 1) % photos.length);
        }
      }, 500);
      
      setDuration(getRandomDuration());
    }, duration);

    return () => clearTimeout(timeout);
  }, [currentIndex, nextIndex, showCurrent, duration, photos.length, imagesLoaded]);

  if (photos.length === 0) {
    return (
      <div className="relative w-full h-full overflow-hidden bg-neutral-800 flex items-center justify-center">
        <p className="text-white/60 tracking-wider">No photos uploaded yet</p>
      </div>
    );
  }

  // Show loading state until images are preloaded
  if (!imagesLoaded) {
    return (
      <div className="relative w-full h-full overflow-hidden bg-black flex items-center justify-center">
        <div className="text-white/40 tracking-wider text-sm">Loading...</div>
      </div>
    );
  }

  const currentPhoto = photos[currentIndex];
  const nextPhoto = photos[nextIndex] || photos[0];

  return (
    <div className="relative w-full h-full overflow-hidden bg-black">
      {/* Current image layer */}
      <motion.div
        key={`current-${currentIndex}`}
        initial={{ opacity: 1 }}
        animate={{ 
          opacity: showCurrent ? 1 : 0,
          scale: showCurrent ? 1.05 : 1
        }}
        transition={{ 
          opacity: { duration: 2, ease: [0.4, 0, 0.2, 1] },
          scale: { duration: duration / 1000, ease: 'linear' }
        }}
        className="absolute inset-0"
        style={{ willChange: 'transform, opacity' }}
      >
        {currentPhoto && (
          <>
            <img
              src={getOptimizedImageUrl(currentPhoto.imageUrl, 'hero')}
              alt={`${currentPhoto.title} — limited edition fine art photography print by Keyiscreation`}
              loading="eager"
              decoding="async"
              width={1920}
              height={1080}
              className="w-full h-full object-cover"
              style={{ 
                imageRendering: 'auto',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)'
              }}
            />
            <div className="absolute inset-0 bg-black/25 pointer-events-none" />
          </>
        )}
      </motion.div>

      {/* Next image layer */}
      {photos.length > 1 && (
        <motion.div
          key={`next-${nextIndex}`}
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: showCurrent ? 0 : 1,
            scale: !showCurrent ? 1.05 : 1
          }}
          transition={{ 
            opacity: { duration: 2, ease: [0.4, 0, 0.2, 1] },
            scale: { duration: duration / 1000, ease: 'linear' }
          }}
          className="absolute inset-0"
          style={{ willChange: 'transform, opacity' }}
        >
          {nextPhoto && (
            <>
              <img
                src={getOptimizedImageUrl(nextPhoto.imageUrl, 'hero')}
                alt={`${nextPhoto.title} — limited edition fine art photography print by Keyiscreation`}
                loading="eager"
                decoding="async"
                width={1920}
                height={1080}
                className="w-full h-full object-cover"
                style={{ 
                  imageRendering: 'auto',
                  backfaceVisibility: 'hidden',
                  transform: 'translateZ(0)'
                }}
              />
              <div className="absolute inset-0 bg-black/25 pointer-events-none" />
            </>
          )}
        </motion.div>
      )}
    </div>
  );
}